"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { safeSessionSet } from "@/lib/browser-storage";
import { buildSEOAgentHref, getSEOIntentConfigForSource } from "@/lib/seo-intents";

interface QueryDomainDef {
  id: string;
  name: string;
  hindi: string;
  primaryHouses: string;
  favorableCombination: string;
  unfavorableCombination: string;
  significatorGraha: string;
  tip: string;
}

const QUERY_DOMAINS: QueryDomainDef[] = [
  {
    id: "job",
    name: "Job, Career & Promotion",
    hindi: "नौकरी और पदोन्नति",
    primaryHouses: "Houses 2, 6, 10, 11",
    favorableCombination:
      "Sub-lord of 6th or 10th cusp connects with 2, 6, 10, 11 (Success & New Job)",
    unfavorableCombination:
      "Connection to 5th or 9th house alone indicates loss of job or resignation",
    significatorGraha: "Sun (Authority) & Saturn (Livelihood)",
    tip: "Auspicious when query cusp lord is placed in direct motion and aspects 10th or 11th house.",
  },
  {
    id: "marriage",
    name: "Marriage & Relationship",
    hindi: "विवाह और प्रेम संबंध",
    primaryHouses: "Houses 2, 7, 11",
    favorableCombination: "Sub-lord of 7th cusp signifies 2 (Family), 7 (Partner), 11 (Fulfilment)",
    unfavorableCombination: "Signification of 1, 6, 10 indicates separation, divorce, or rejection",
    significatorGraha: "Venus (for men) / Jupiter (for women)",
    tip: "If Moon transits benefic nakshatras at question time, marriage negotiations finalize quickly.",
  },
  {
    id: "health",
    name: "Health & Disease Recovery",
    hindi: "स्वास्थ्य और रोग मुक्ति",
    primaryHouses: "Houses 1, 5, 11 (Health) vs 6, 8, 12 (Disease)",
    favorableCombination: "Sub-lord of 1st cusp signifies 1, 5, 11 (Fast Recovery & High Vitality)",
    unfavorableCombination:
      "Sub-lord of 6th cusp signifies 6, 8, 12 (Prolonged treatment required)",
    significatorGraha: "Sun (Vitality) & Moon (Mind/Fluids)",
    tip: "Check the Lagna lord of this moment; strong Lagna ensures fast cure with right medication.",
  },
  {
    id: "property",
    name: "Property Purchase & Real Estate",
    hindi: "मकान, जमीन और वाहन",
    primaryHouses: "Houses 4, 11, 12",
    favorableCombination: "Sub-lord of 4th cusp signifies 4 (Property), 11 (Gain), 12 (Investment)",
    unfavorableCombination:
      "Signification of 3, 6 indicates cancellation of deed or disputed title",
    significatorGraha: "Mars (Bhoomi Karaka) & Venus (Vehicles)",
    tip: "Ensure 4th house is free from Rahu affliction at query time for clean legal documentation.",
  },
  {
    id: "legal",
    name: "Court Cases & Disputes",
    hindi: "मुकदमा और विवाद विजय",
    primaryHouses: "Houses 6, 11 (Victory) vs 12 (Loss)",
    favorableCombination:
      "Sub-lord of 6th cusp signifies 6 (Winning over opponent) and 11 (Triumph)",
    unfavorableCombination:
      "Signification of 12th house indicates settlement compromise or penalties",
    significatorGraha: "Mars (Litigation) & Jupiter (Justice)",
    tip: "A strong 11th house from query ascendant indicates victory for the questioner.",
  },
];

export default function PrashnaKpStudio() {
  const [selectedId, setSelectedId] = useState<string>("job");

  const domain = useMemo<QueryDomainDef>(() => {
    return QUERY_DOMAINS.find((d) => d.id === selectedId) || QUERY_DOMAINS[0];
  }, [selectedId]);

  const handleConsult = () => {
    const prompt = `I have a specific Horary question about ${domain.name} (${domain.hindi}). The Prashna time is right now. Please cast the Horary chart and analyze the ruling planets and cusp sub-lords for a definitive answer.`;
    try {
      safeSessionSet("seo_entry_intent", "prashna_lookup");
      safeSessionSet("seo_entry_path", "/prashna-kundali");
      safeSessionSet("seo_entry_prompt", prompt);
      safeSessionSet("seo_entry_auto_send", "1");
    } catch {
      // Storage fallback
    }
  };

  return (
    <div className="rounded-2xl border border-border-default bg-surface-raised p-5 sm:p-8 shadow-sm my-8">
      <StudioHeader />
      <DomainSelector selectedId={selectedId} onSelect={setSelectedId} />
      <DomainDetailCard domain={domain} onConsult={handleConsult} />
    </div>
  );
}

function StudioHeader() {
  return (
    <div className="text-center max-w-2xl mx-auto mb-6">
      <span className="text-eyebrow font-bold uppercase tracking-wider text-intent-brand">
        KP Horary Astrology
      </span>
      <h2 className="text-title font-bold text-text-primary mt-1 font-serif">
        Prashna Kundali &amp; KP Horary Sub-Lord Check (प्रश्न कुंडली)
      </h2>
      <p className="text-body-small text-text-secondary mt-1">
        Check question outcome by time of query using Krishnamurti Paddhati (KP) cuspal sub-lords —
        no birth time needed.
      </p>
    </div>
  );
}

function DomainSelector({
  selectedId,
  onSelect,
}: {
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  return (
    <div className="mb-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        {QUERY_DOMAINS.map((d) => {
          const isSelected = d.id === selectedId;
          return (
            <button
              key={d.id}
              type="button"
              onClick={() => onSelect(d.id)}
              className={`p-3 rounded-xl border text-left transition-all ${
                isSelected
                  ? "border-intent-brand bg-surface-brand-subtle shadow-sm ring-2 ring-intent-brand/20 text-text-primary"
                  : "border-border-default bg-surface-raised hover:bg-surface-warm text-text-secondary"
              }`}
            >
              <span className="block text-body-small font-bold text-text-primary">
                {d.name.split(",")[0]}
              </span>
              <span className="block text-caption text-text-muted">{d.hindi}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function DomainDetailCard({
  domain,
  onConsult,
}: {
  domain: QueryDomainDef;
  onConsult: () => void;
}) {
  const chatHref = buildSEOAgentHref(
    getSEOIntentConfigForSource("/prashna-kundali"),
    "/prashna-kundali",
    "en"
  );

  return (
    <div className="rounded-xl border border-border-default bg-surface-raised p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-5 border-b border-border-default">
        <div>
          <div className="flex items-center gap-3">
            <h3 className="text-xl font-bold font-serif text-text-primary">
              {domain.name} ({domain.hindi})
            </h3>
            <span className="px-3 py-0.5 rounded-full text-caption font-bold bg-intent-brand/10 text-intent-brand">
              {domain.primaryHouses}
            </span>
          </div>
          <p className="text-caption text-text-secondary mt-1">
            Significator Grahas:{" "}
            <strong className="text-text-primary">{domain.significatorGraha}</strong>
          </p>
        </div>

        <a
          href={chatHref}
          onClick={onConsult}
          className="shrink-0 inline-flex items-center justify-center rounded-lg bg-surface-brand-strong px-5 py-2.5 text-button font-bold text-text-on-brand hover:bg-surface-brand-strong-hover transition-colors shadow-sm"
        >
          Ask Horary Question Now ✦
        </a>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
        <div className="p-4 rounded-xl bg-success/5 border border-success/20">
          <p className="text-caption font-bold uppercase tracking-wider text-success mb-1.5">
            Favorable Cusp Rule (सकारात्मक परिणाम)
          </p>
          <p className="text-caption text-text-primary leading-relaxed">
            {domain.favorableCombination}
          </p>
        </div>

        <div className="p-4 rounded-xl bg-warning/5 border border-warning/20">
          <p className="text-caption font-bold uppercase tracking-wider text-warning mb-1.5">
            Obstructive Cusp Rule (सावधानी)
          </p>
          <p className="text-caption text-text-primary leading-relaxed">
            {domain.unfavorableCombination}
          </p>
        </div>
      </div>

      <div className="mt-4 p-4 rounded-xl bg-surface-raised border border-border-default">
        <p className="text-caption font-bold uppercase tracking-wider text-intent-brand mb-1">
          KP Jyotishi Rule of This Moment
        </p>
        <p className="text-caption text-text-secondary leading-relaxed">{domain.tip}</p>
      </div>

      <div className="mt-5 pt-4 border-t border-border-default flex flex-col sm:flex-row items-center justify-between gap-3 text-caption">
        <span className="text-text-secondary">
          No birth details needed — Prashna Kundali reads the celestial chart of the moment you ask.
        </span>
        <Link
          href="/prashna-kundali/"
          className="font-semibold text-intent-brand hover:underline flex items-center gap-1"
        >
          Cast Live Prashna Chart →
        </Link>
      </div>
    </div>
  );
}
